import React, { useState } from "react";
import CandidateCard from "./CandidateCard";
import CandidateProfile from "./CandidateProfile";
import Pipeline from "./Pipeline";
import EmptyCandidates from "./EmptyCandidate";
import { PIPELINE_STEPS } from "variables/constants";
import SearchBar from "components/search/SearchBar";
import SelectOption from "components/select/SelectOption";
import {
    Users,
} from "lucide-react";
import Card from "components/card";

const CandidateBoard = () => {
    const [selected, setSelected] = useState(null);
    const [stageFilter, setStageFilter] = useState(0); 
    const [positionFilter, setPositionFilter] = useState(0);

    const initialCandidates = [
        {
        id: 1,
        name: "John Doe",
        role: "Frontend Developer",
        location: "Cebu City",
        experience: 3,
        skills: ["React", "Tailwind", "Redux"],
        email: "john.doe@email.com",
        phone: "+63 912 345 6789",
        education: "BS Computer Science - USC",
        applied: "Oct 25, 2025",
        notes: "Strong frontend foundation, prefers clean UI systems.",
        stage: "Application",
        code: "CND004"
        },
        {
        id: 2,
        name: "Jane Smith",
        role: "Senior Software Engineer",
        location: "Manila",
        experience: 5,
        skills: ["Laravel", "MySQL", "API"],
        email: "jane.smith@email.com",
        phone: "+63 921 123 4567",
        education: "BS Information Technology - DLSU",
        applied: "Oct 18, 2025",
        notes: "Solid API design skills, learning microservices.",
        stage: "Offer",
        code: "CND008"
        },
        {
        id: 3,
        name: "Alex Tan",
        role: "UX/UI Designer",
        location: "Davao",
        experience: 4,
        skills: ["Figma", "CSS", "Wireframes", "SQL"],
        email: "alex.tan@email.com",
        phone: "+63 922 222 9999",
        education: "BA Multimedia Arts - Ateneo",
        applied: "Oct 10, 2025",
        notes: "Excellent portfolio, creative thinker.",
        stage: "Client Endorsement",
        code: "CND007"
        },
    ];

    const [candidates, setCandidates] = useState(initialCandidates);

    const stageMap = {
        1: "Application",
        2: "Screening",
        3: "Testing",
        4: "Shortlisting",
        5: "Client Endorsement",
        6: "Client Selection",
        7: "References",
        8: "Offer",
        9: "Onboarding",
    };

    const positionMap = {
        1: "Senior Software Engineer",
        2: "UX/UI Designer",
        3: "Product Manager",
        4: "Data Scientist",
        5: "DevOps Engineer",
    };

    const stagesInOrder = Object.values(stageMap);

    const getProgressByStage = (stage) => {
        const index = stagesInOrder.indexOf(stage);
        if (index === -1) return 0;
        return Math.round(((index + 1) / stagesInOrder.length) * 100);
    };

    const pipelineCounts = Object.keys(PIPELINE_STEPS).map((name) => ({
        name,
        icon: PIPELINE_STEPS[name],
        count: candidates.filter((c) => c.stage === name).length,
    }));

    const filteredCandidates = candidates.filter((candidate) => {
        const stageMatch = stageFilter === 0 || candidate.stage === stageMap[stageFilter];
        const positionMatch =
            positionFilter === 0 || candidate.role === positionMap[positionFilter];
        return stageMatch && positionMatch;
    });

    const advanceStage = (candidate) => {
        const currentIndex = stagesInOrder.indexOf(candidate.stage);
        if (currentIndex === -1) return candidate.stage;
        if (currentIndex === stagesInOrder.length - 1) return candidate.stage; 
        return stagesInOrder[currentIndex + 1];
    };

    const handleAdvanceConfirmFromProfile = () => {
        if (!selected) return;

        const updatedStage = advanceStage(selected);

        const updatedCandidates = candidates.map((c) =>
            c.id === selected.id ? { ...c, stage: updatedStage } : c
        );

        setCandidates(updatedCandidates);

        setSelected({ ...selected, stage: updatedStage });
    };

    return (
        <div className="flex flex-col">
            <div className="space-y-6">
                <Pipeline steps={pipelineCounts} />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="col-span-2">
                        <Card className="flex flex-col gap-6 rounded-2xl shadow-sm bg-white border-2 border-slate-200">
                            <div className="bg-gradient-to-br from-emerald-50 to-cyan-50 border-b rounded-t-2xl p-6 grid items-start gap-1.5">
                                <div className="flex items-center justify-between gap-2">
                                    <div className="min-w-0">
                                        <h4 className="text-[18px]">
                                            Active Candidates
                                        </h4>
                                        <p className="text-[14px] text-slate-500">
                                            {filteredCandidates.length} candidates in pipeline
                                        </p>
                                    </div>
                                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-lg px-2 py-0.5 font-medium  bg-gradient-to-br from-emerald-500 to-cyan-500 text-white text-[13px] flex-shrink-0">
                                        {filteredCandidates.length}
                                    </span>
                                </div>
                            </div>
                            <div className="pb-6 p-6 space-y-3">
                                <SearchBar
                                    placeholder="Search candidates..." 
                                />

                                <div className="grid grid-cols-2 gap-x-2">
                                    <SelectOption
                                        placeholder="All Stages"
                                        options={[
                                            { label: "All Stages", value: 0 },
                                            { label: "Application", value: 1 },
                                            { label: "Screening", value: 2 },
                                            { label: "Testing", value: 3 },
                                            { label: "Shortlisting", value: 4 },
                                            { label: "Client Endorsement", value: 5 },
                                            { label: "Client Selection", value: 6 },
                                            { label: "References", value: 7 },
                                            { label: "Offer", value: 8 },
                                            { label: "Onboarding", value: 9 },
                                        ]}
                                        value={stageFilter}
                                        onChange={setStageFilter}
                                    />
                                    <SelectOption
                                        placeholder="All Positions"
                                        options={[
                                            { label: "All Positions", value: 0 },
                                            { label: "Senior Software Engineer", value: 1 },
                                            { label: "UX/UI Designer", value: 2 },
                                            { label: "Product Manager", value: 3 },
                                            { label: "Data Scientist", value: 4 },
                                            { label: "DevOps Engineer", value: 5 },
                                        ]}
                                        value={positionFilter}
                                        onChange={setPositionFilter}
                                    />
                                </div>

                                <div className="w-full h-[1px] bg-slate-300"></div>

                                <div className="grid grid-cols-1 gap-4 overflow-y-auto max-h-[450px]">
                                    {filteredCandidates.length === 0 ? (
                                        <EmptyCandidates />
                                    ) : (
                                        filteredCandidates.map((candidate) => (
                                            <CandidateCard
                                                key={candidate.id}
                                                {...candidate}
                                                progress={getProgressByStage(candidate.stage)}
                                                selected={selected?.id === candidate.id}
                                                onClick={() => setSelected(candidate)}
                                                stage={candidate.stage}
                                            />
                                        ))
                                    )}
                                </div>
                            </div>
                        </Card>
                    </div>

                    <div className="hidden lg:block">
                        {selected ? (
                            <CandidateProfile 
                                candidate={selected} 
                                onAdvance={handleAdvanceConfirmFromProfile}
                            />
                            ) : (
                            <div className="flex flex-col items-center justify-center h-[200px] text-gray-500 text-sm bg-white border-2 rounded-xl border-slate-300 border-dashed pb-6 px-4 py-12 text-center">
                                <Users className="w-12 h-12 text-gray-800 mb-3" />
                                <h4 className="text-[14px] font-semibold mb-2">
                                    No Candidate Selected
                                </h4>
                                <p className="text-[12px] max-w-xs">
                                    Select a candidate from the list to view their details and progress
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CandidateBoard;